import { HugeiconsIcon } from '@hugeicons/react'
import { CheckmarkCircle01Icon } from '@hugeicons/core-free-icons'

import type { TrajectoryResponsePositionTrajectoriesItem } from '@/types/api-compat'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

export interface TrajectoryPathVisualizationProps {
  trajectoryPath: Array<TrajectoryResponsePositionTrajectoriesItem>
  pathName: string
  currentLevel?: string
  className?: string
}

export const TrajectoryPathVisualization = ({
  trajectoryPath,
  pathName,
  currentLevel,
  className,
}: TrajectoryPathVisualizationProps) => {
  // Extract all unique positions in order
  const positions = trajectoryPath
    .sort((a, b) => a.progressionOrder - b.progressionOrder)
    .map((item) => item.fromPosition)

  // Add the final toPosition if it exists (may be null at runtime despite generated types)
  const lastItem = trajectoryPath[trajectoryPath.length - 1]
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (lastItem.toPosition) {
    positions.push(lastItem.toPosition)
  }

  // Find current position index
  const currentIndex = currentLevel
    ? positions.findIndex((pos) => pos.level === currentLevel)
    : -1

  return (
    <div className={cn('p-6 bg-muted/30 rounded-lg', className)}>
      <h2 className="text-2xl text-center font-semibold text-foreground mb-4">
        {pathName}
      </h2>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-2">
        {positions.map((position, index) => {
          const isCurrent = index === currentIndex
          const isPassed = currentIndex !== -1 && index < currentIndex
          const isFuture = currentIndex !== -1 && index > currentIndex

          return (
            <Badge
              key={index}
              variant={
                isCurrent ? 'default' : isPassed ? 'secondary' : 'outline'
              }
              className={cn(
                'w-full px-4 py-3 text-sm font-medium transition-all justify-center',
                isCurrent && 'bg-primary text-primary-foreground',
                isPassed &&
                  'bg-primary/20 text-primary dark:bg-primary/30 dark:text-primary-foreground',
                isFuture && 'opacity-60',
              )}
            >
              <div className="flex items-center gap-2">
                {isPassed && (
                  <HugeiconsIcon
                    icon={CheckmarkCircle01Icon}
                    className="h-3 w-3"
                  />
                )}
                <span>{position.level}</span>
              </div>
            </Badge>
          )
        })}
      </div>
    </div>
  )
}
